import React, { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { HomeViewModel } from '../../viewModels/HomeViewModel';
import { Banner } from '../../shared/models/Banner';
import { Project } from '../../shared/models/Project';
import { ProjectProgress } from '../../shared/models/ProjectProgress';
import { TopBar } from '../../components/TopBar/TopBar';
import { ProjectList } from '../../components/ProjectList/ProjectList';
import { OrganizationHeader } from '../../components/OrganizationHeader/OrganizationHeader';
import { BannerCarousel } from '../../components/BannerCarousel/BannerCarousel';
import { ViewMoreButton } from '../../components/ViewMoreButton/ViewMoreButton';
import { ProgressTimeline, ProgressTimelineItem } from '../../components/ProgressTimeline';
import { useOrganization } from '../../contexts/OrganizationContext';
import '../../styles/section.css';
import './HomeView.css';

interface HomeViewProps {
  viewModel: HomeViewModel;
  /** 点击"查看更多"时，通知外层切到项目列表 */
  onViewAllProjects?: (payload: {
    projects: Project[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
    hasMore: boolean;
    banners: Banner[];
  }) => void;
  onProjectClick?: (project: Project) => void;
  onProgressClick?: (progress: ProjectProgress) => void;
  /** 点击机构信息时，跳转到机构详情页 */
  onOrganizationClick?: (organizationId: string, organization?: any) => void;
}

/**
 * 首页视图（H5 用户端）
 */
export const HomeView: React.FC<HomeViewProps> = observer(({ viewModel, onViewAllProjects, onProjectClick, onProgressClick, onOrganizationClick }) => {
  const { organization } = useOrganization();

  useEffect(() => {
    if (organization) {
      viewModel.init(organization.id, organization.type);
    }
    
    return () => {
      viewModel.reset();
    };
  }, [viewModel, organization]);

  // 防护：未加载到机构时不渲染依赖 organization 的内容，避免白屏
  if (!organization) {
    return (
      <div className="home-view">
        <TopBar title="首页" showBack={false} />
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>加载中...</div>
      </div>
    );
  }

  // 处理查看全部
  const handleViewAll = () => {
    onViewAllProjects?.({
      projects: viewModel.projects,
      pagination: viewModel.projectPagination,
      hasMore: viewModel.projectHasMore,
      banners: viewModel.banners,
    });
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return dateStr.length >= 10 ? dateStr.slice(0, 10) : dateStr;
  };

  return (
    <div className="home-view">
      {/* TopBar */}
      <TopBar title="首页" showBack={false} />

      {/* 机构信息 */}
      <OrganizationHeader />

      {/* Banner 轮播图 */}
      <BannerCarousel
        banners={viewModel.banners}
        loading={viewModel.bannerLoading}
      />

      {/* 当没有项目时，显示占位图片 */}
      {!viewModel.projectLoading && (!viewModel.projects || viewModel.projects.length === 0) ? (
        <>
          <div className="home-empty-state">
            <img src="/opening.svg" alt="暂无项目" className="home-empty-image" />
          </div>
          {/* 占位卡片（没有项目时也显示） */}
          {organization && (
            <div 
              className="progress-placeholder-card placeholder-card-clickable"
              onClick={() => onOrganizationClick?.(organization.id, organization)}
            >
              {/* 第一行：机构 logo + 机构名称 */}
              <div className="placeholder-card-header">
                {organization.logoUrl && (
                  <img
                    src={organization.logoUrl}
                    alt={organization.name}
                    className="placeholder-card-logo"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="placeholder-card-title">{organization.name}</div>
              </div>
              {/* 第二行：机构简介 */}
              {organization.introduction && (
                <div className="placeholder-card-desc">{organization.introduction}</div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* 项目列表 */}
          <div className="project-section">
            <div className="section-header">
              <h2>公益项目</h2>
              {viewModel.projectHasMore && <span className="section-tag">还有更多</span>}
            </div>

            <ProjectList
              projects={viewModel.displayProjects}
              loading={viewModel.projectLoading}
              emptyText={`暂无项目 (数量: ${viewModel.projects?.length || 0})`}
              onItemClick={onProjectClick}
            />
            {viewModel.projects && viewModel.projects.length > 0 && (
              <ViewMoreButton
                text="查看更多"
                iconType="right"
                onClick={handleViewAll}
              />
            )}
          </div>

          {/* 项目最新进展时间轴 */}
          <div className="progress-section">
            <div className="section-header">
              <h2>项目最新进展</h2>
              {viewModel.progressHasMore && <span className="section-tag">还有更多</span>}
            </div>

            {viewModel.progressLoading ? (
              <div className="project-loading">加载中...</div>
            ) : viewModel.progresses && viewModel.progresses.length > 0 ? (
              <ProgressTimeline>
                {viewModel.progresses.map((item: ProjectProgress) => {
                  // 虚拟进展（启动公益计划）不可点击
                  const isVirtual = item.id.startsWith('virtual-launch-');
                  return (
                  <ProgressTimelineItem
                    key={item.id}
                    date={formatDate(item.progressDate)}
                      onClick={isVirtual ? undefined : () => onProgressClick?.(item)}
                  >
                    <div className="timeline-card">
                      <div className="timeline-project">{item.projectName || item.projectId}</div>
                      <div className="timeline-title">{item.title}</div>
                      {item.images && item.images.length > 0 && (
                        <div className="timeline-images">
                          {item.images.slice(0, 3).map((img, idx) => (
                            <img key={idx} src={img} alt={item.title} loading="lazy" />
                          ))}
                        </div>
                      )}
                    </div>
                  </ProgressTimelineItem>
                  );
                })}
                {viewModel.progressHasMore && (
                  <div className="timeline-more">
                    <ViewMoreButton
                      text="查看更多"
                      onClick={() => viewModel.loadMoreProgresses()}
                    />
                  </div>
                )}
              </ProgressTimeline>
            ) : (
              <div className="project-empty">暂无项目进展</div>
            )}

            {/* 占位卡片（始终显示在项目进展下方） */}
            {!viewModel.progressLoading && organization && (
              <div 
                className="progress-placeholder-card placeholder-card-clickable"
                onClick={() => onOrganizationClick?.(organization.id, organization)}
              >
                {/* 第一行：机构 logo + 机构名称 */}
                <div className="placeholder-card-header">
                  {organization.logoUrl && (
                    <img
                      src={organization.logoUrl}
                      alt={organization.name}
                      className="placeholder-card-logo"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  )}
                  <div className="placeholder-card-title">{organization.name}</div>
                </div>
                {/* 第二行：机构简介 */}
                {organization.introduction && (
                  <div className="placeholder-card-desc">{organization.introduction}</div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* 常驻提示/机构卡片 */}
      {viewModel.organization && (
        <div className="hint-card">
          <div className="hint-header">
            <img
              src={viewModel.organization.logoUrl || ''}
              alt={viewModel.organization.name}
              className="hint-logo"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="hint-title">{viewModel.organization.name}</div>
          </div>
          <div className="hint-body">
            <p className="hint-desc">
              {viewModel.organization.description || '公益计划上线中'}
            </p>
            <button className="link-btn">前往官网</button>
          </div>
        </div>
      )}

      {viewModel.error && (
        <div className="error-message" style={{ margin: '12px', padding: '12px', background: '#fff2f0', border: '1px solid #ffccc7', borderRadius: '4px', color: '#ff4d4f', fontSize: '14px', textAlign: 'center' }}>
          {viewModel.error}
        </div>
      )}
    </div>
  );
});

